import React from 'react';
import { 
  CheckCircle, MessageSquare, AlertCircle
} from 'lucide-react';

/**
 * BookingStatusSection Component
 * Handles rendering of booking status fields (availabilityStatus, note)
 * Includes cancellation confirmation for cancel status
 * Extracted from SimplifiedReservationForm for better code organization
 */
const BookingStatusSection = ({
  reservationData,
  handleEnhancedInputChange,
  fieldValidation,
  reservationSettings,
  isSaving,
  uploadingDocuments
}) => {
  
  // Get icon component for field
  const getFieldIcon = (iconName) => {
    const icons = {
      CheckCircle,
      MessageSquare
    };
    return icons[iconName] || CheckCircle;
  };

  // Get booking status fields from settings
  const getBookingStatusFields = () => {
    const fieldSettings = reservationSettings.fieldSettings || {};
    const bookingFieldKeys = ['availabilityStatus', 'note'];
    
    return Object.entries(fieldSettings)
      .filter(([fieldKey, fieldConfig]) => 
        fieldConfig && 
        fieldConfig.visible && 
        bookingFieldKeys.includes(fieldKey)
      )
      .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
  };

  // Render individual booking status field
  const renderBookingField = (fieldKey, fieldConfig) => {
    if (!fieldConfig || !fieldConfig.visible) return null;

    const value = reservationData[fieldKey] || '';
    const validation = fieldValidation[fieldKey];
    const hasError = validation && !validation.isValid;
    const IconComponent = getFieldIcon(fieldConfig.icon);

    const commonProps = {
      name: fieldKey,
      value: value,
      onChange: handleEnhancedInputChange,
      disabled: isSaving || uploadingDocuments,
      required: fieldConfig.required,
      className: `w-full p-3 bg-gray-700 border text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 transition-colors ${
        hasError ? 'border-red-500 bg-red-900/20' : 'border-gray-600'
      } ${reservationSettings.formBehavior?.compactMode ? 'p-2 text-sm' : 'p-3'}`,
      placeholder: fieldConfig.placeholder || `Enter ${fieldConfig.label.toLowerCase()}`
    };

    const renderFieldInput = () => {
      switch (fieldKey) {
        case 'availabilityStatus':
          const allowedValues = fieldConfig.allowedValues || ['booked', 'pending', 'cancel'];
          return (
            <select 
              {...commonProps}
              className={`${commonProps.className} ${
                value === 'cancel' ? 'border-red-500 bg-red-900/20' : ''
              }`}
            >
              {allowedValues.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          );

        case 'note':
          const maxLength = fieldConfig.maxLength || reservationSettings.validationRules?.noteMaxLength || 500;
          return (
            <div>
              <textarea
                {...commonProps}
                maxLength={maxLength}
                className={`${commonProps.className} h-24 resize-none`}
                rows={reservationSettings.formBehavior?.compactMode ? 3 : 4}
              />
              {reservationSettings.formBehavior?.showCharacterCounts && (
                <div className="text-xs text-gray-400 mt-1 text-right">
                  {value.length}/{maxLength}
                </div>
              )}
            </div>
          );

        default:
          return (
            <input
              type="text"
              {...commonProps}
            />
          );
      }
    };

    return (
      <div key={fieldKey} className={reservationSettings.formBehavior?.compactMode ? 'space-y-1' : 'space-y-2'}>
        <label className="block text-sm font-medium text-gray-300">
          <div className="flex items-center gap-2">
            {reservationSettings.formBehavior?.showFieldIcons && (
              <IconComponent size={16} className="text-orange-400" />
            )}
            <span>
              {fieldConfig.label} 
              {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
            </span>
            
            {fieldKey === 'availabilityStatus' && value === 'cancel' && (
              <span className="text-xs text-red-400">(Cancelled)</span>
            )}
          </div>
          
          {reservationSettings.formBehavior?.showFieldDescriptions && fieldConfig.description && (
            <div className="text-xs text-gray-500 mt-1 font-normal">
              {fieldConfig.description}
            </div>
          )}
        </label>
        
        {renderFieldInput()}
        
        {hasError && validation.errors.length > 0 && (
          <div className="space-y-1">
            {validation.errors.map((error, index) => (
              <div key={index} className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle size={12} />
                <span>{error}</span>
              </div>
            ))}
          </div>
        )}

        {fieldKey === 'availabilityStatus' && value === 'cancel' && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mt-2">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-300">
                <div className="font-medium mb-1">Cancellation Notice</div>
                <div className="text-xs">
                  This reservation will be marked as cancelled when saved. This action cannot be undone.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const bookingFields = getBookingStatusFields();

  // Don't render section if no fields are visible
  if (bookingFields.length === 0) return null;

  return (
    <div className={reservationSettings.formBehavior?.compactMode ? 'space-y-3' : 'space-y-4'}>
      {/* Section Header */}
      {reservationSettings.formBehavior?.groupRelatedFields && (
        <div className="border-b border-gray-700 pb-2">
          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            Booking Status
          </h4>
          {reservationSettings.formBehavior?.showFieldDescriptions && (
            <p className="text-gray-400 text-sm mt-1">
              {bookingFields.length} field{bookingFields.length !== 1 ? 's' : ''} • Reservation status and notes
            </p>
          )}
        </div>
      )}
      
      {/* Booking Status Fields Grid */}
      <div className={`grid gap-${reservationSettings.formBehavior?.compactMode ? '4' : '6'} ${
        reservationSettings.formBehavior?.compactMode 
          ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
          : 'grid-cols-1 md:grid-cols-2'
      }`}>
        {bookingFields.map(([fieldKey, fieldConfig]) => renderBookingField(fieldKey, fieldConfig))}
      </div>
    </div>
  );
};

export default BookingStatusSection;